import React from 'react';
import {
  Box,
  Button,
  Flex,
  HStack,
  IconButton,
  Link,
  Menu,
  MenuButton,
  MenuItem,
  MenuList,
  useDisclosure,
} from '@chakra-ui/react';
import { Link as RouterLink, useLocation } from 'react-router-dom';
import { MdExpandMore, MdMenu } from 'react-icons/md';
import Container from '../ui/Container';
import MobileMenu from './MobileMenu';
import { MAIN_NAV, PRODUCT_CATEGORIES } from '../../config/navigation';
import { SERVICES } from '../../data/services';
import SiesaLogo from '../ui/SiesaLogo';

const MENUS = {

  services: SERVICES.map(s => ({ label: s.title, href: '/services' })),
  products: [
    ...PRODUCT_CATEGORIES.map(c => ({
      label: c.label,
      href: `/products/${c.slug}`,
    })),
  ],
  projects: [
    { label: 'Galería de proyectos', href: '/projects' },
    { label: 'Siesa QR App', href: '/siesa-qr-app' },
  ],
};

function NavLink({ item, isActive }) {
  return (
    <Link
      as={RouterLink}
      to={item.href}
      variant="nav"
      px={3}
      py={2}
      fontSize="sm"
      color={isActive ? 'accent.default' : 'text.primary'}
      borderBottom="3px solid"
      borderColor={isActive ? 'accent.default' : 'transparent'}
      aria-current={isActive ? 'page' : undefined}
    >
      {item.label}
    </Link>
  );
}

function NavMenu({ item, isActive }) {
  const entries = MENUS[item.menu];
  return (
    <Menu placement="bottom-start" autoSelect={false} variant="publicSurface">
      <MenuButton
        as={Button}
        variant="text"
        px={3}
        fontSize="sm"
        color={isActive ? 'accent.default' : 'text.primary'}
        rightIcon={<MdExpandMore aria-hidden="true" />}
        borderBottom="3px solid"
        borderColor={isActive ? 'accent.default' : 'transparent'}
        borderRadius={0}
      >
        {item.label}
      </MenuButton>
      <MenuList
        bg="white"
        color="text.primary"
        py={2}
        boxShadow="lg"
        borderColor="border.default"
        borderRadius="lg"
        minW="250px"
        zIndex="dropdown"
      >
        {entries.map(entry => (
          <MenuItem
            key={entry.label}
            as={RouterLink}
            to={entry.href}
            fontSize="sm"
            bg="white"
            color="text.primary"
            minH="44px"
            _hover={{ bg: 'surface.blue', color: 'brand.700' }}
            _focus={{ bg: 'surface.blue', color: 'brand.700' }}
          >
            {entry.label}
          </MenuItem>
        ))}
      </MenuList>
    </Menu>
  );
}

export default function Navbar() {
  const { isOpen, onOpen, onClose } = useDisclosure();
  const { pathname } = useLocation();

  const isActive = href =>
    href === '/' ? pathname === '/' : pathname.startsWith(href);

  return (
    <Box
      as="header"
      position="sticky"
      top={0}
      zIndex="sticky"
      bg="surface.default"
      borderBottom="1px solid"
      borderColor="border.default"
    >
      <Container>
        <Flex
          align="center"
          justify="space-between"
          h={{ base: '84px', lg: '104px' }}
          gap={4}
        >
          <Link
            as={RouterLink}
            to="/"
            aria-label="SIESA, ir al inicio"
            flexShrink={0}
          >
            <SiesaLogo
              variant="navbar"
              h={{ base: '70px', sm: '84px', lg: '96px' }}
              w="auto"
              maxH={{ base: '76px', sm: '90px', lg: '102px' }}
            />
          </Link>

          <HStack
            as="nav"
            aria-label="Navegación principal"
            display={{ base: 'none', lg: 'flex' }}
            spacing={1}
          >
            {MAIN_NAV.map(item =>
              item.menu ? (
                <NavMenu
                  key={item.label}
                  item={item}
                  isActive={isActive(item.href)}
                />
              ) : (
                <NavLink
                  key={item.label}
                  item={item}
                  isActive={isActive(item.href)}
                />
              )
            )}
          </HStack>

          <HStack spacing={2} flexShrink={0}>
            <Button
              as={RouterLink}
              to="/contact?subject=quote"
              variant="primary"
              display={{ base: 'none', md: 'inline-flex' }}
            >
              Solicitar cotización
            </Button>
            <IconButton
              aria-label="Abrir el menú de navegación"
              icon={<MdMenu size={24} />}
              variant="secondary"
              display={{ base: 'inline-flex', lg: 'none' }}
              onClick={onOpen}
            />
          </HStack>
        </Flex>
      </Container>

      <MobileMenu isOpen={isOpen} onClose={onClose} />
    </Box>
  );
}
